import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import DeleteIcon from '@mui/icons-material/Delete';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SearchIcon from '@mui/icons-material/Search';
import { Button, Divider, Grid, IconButton, LinearProgress, Stack } from '@mui/material';
import type { GridRowId, GridRowModesModel, GridRowsProp } from '@mui/x-data-grid';
import { GridRowModes } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import type { HistoricoArete } from '../../api-client/pre-compras/aretes.dto';
import type { Zona } from '../../api-client/zonas/zona';
import type { Corral } from '../../stores/corrales/corral';
import type { PersonaEntregaResumen } from '../../stores/personal-entrega/persona-entrega';
import type { PersonaRecibeResumen } from '../../stores/personal-recibe/persona-recibe';
import type { PersonaVendeResumen } from '../../stores/personal-vende/persona-vende';
import type { PreCompra, PreCompraBusqueda } from '../../stores/pre-compras/pre-compra';
import type { TipoGanado } from '../../stores/tipos-ganado/tipo-ganado';
import { useUIStore } from '../../stores/ui/ui.store';
import generateRandonId from '../../utils/generate-random-id';
import transformToOptions from '../../utils/transform-to-options';
import PersonalEntregaAddDialog from '../personal-entrega/PersonalEntregaAddDialog';
import PersonalEntregaSearchDialog from '../personal-entrega/PersonalEntregaSearchDialog';
import PersonalRecibeAddDialog from '../personal-recibe/PersonalRecibeAddDialog';
import PersonalRecibeSearchDialog from '../personal-recibe/PersonalRecibeSearchDialog';
import PersonalVendeAddDialog from '../personal-vende/PersonalVendeAddDialog';
import PersonalVendeSearchDialog from '../personal-vende/PersonalVendeSearchDialog';
import AppDialog from '../ui/AppDialog';
import AppForm from '../ui/AppForm';
import AppTextField from '../ui/AppTextField';
import ComboBox from '../ui/Combobox';
import HistorialAreteDialog from '../ui/HistorialAreteDialog';
import AnimalesPreCompra from './AnimalesPreCompra';
import type { AnimalPreCompraRow } from './AnimalPreCompraRow';
import {
  validationSchema,
  type AnimalPreCompra,
  type PreCompraFormValues
} from './pre-compra.schema';
import PreCompraSearchDialog from './PreCompraSearchDialog';

interface PreCompraFormProps {
  precompra: PreCompra;
  tiposGanado: Array<TipoGanado>;
  corrales: Array<Corral>;
  historialSiiniga: Array<HistoricoArete>;
  historialTemporal: Array<HistoricoArete>;
  zonas: Array<Zona>;
  esAgregar?: boolean;
  cargarHistorialSiiniga: (folio: string, partida: number) => Promise<void>;
  cargarHistorialTemporal: (folio: string, partida: number) => Promise<void>;
  // reloadPersonalVende: () => Promise<void>;
  // reloadPersonalRecibe: () => Promise<void>;
  // reloadPersonalEntrega: () => Promise<void>;
  onAdd?: () => Promise<void>;
  onCancel?: () => Promise<void>;
  onSave: (values: PreCompraFormValues) => Promise<boolean>;
  onPreCompraSelected: (precompra: PreCompraBusqueda) => void;
}

function convertToAnimalesPreCompraRow(precompra: PreCompra): Array<AnimalPreCompraRow> {
  const partidas = precompra?.partidas;
  return !partidas?.length
    ? []
    : partidas.map((p) => ({
        id: generateRandonId(),
        folio: precompra?.folioInterno ?? '',
        partida: p.partida,
        areteSiiniga: p.areteSiiniga,
        areteTemporal: p.areteTemporal,
        tienePapeles: p.tienePapeles,
        reemo: p.reemo,
        idTipoGanado: p.idTipoGanado,
        kgCompra: p.kgCompra,
        kgLlegada: p.kgLlegada,
        isNew: false
      }));
}

function convertToRowsModel(animales: Array<AnimalPreCompraRow>) {
  return animales.reduce((acc, animal) => {
    acc[animal.id] = { mode: GridRowModes.View };
    return acc;
  }, {} as GridRowModesModel);
}

const PreCompraForm = ({
  precompra,
  tiposGanado,
  corrales,
  historialSiiniga,
  historialTemporal,
  zonas,
  esAgregar,
  cargarHistorialSiiniga,
  cargarHistorialTemporal,
  // reloadPersonalVende,
  // reloadPersonalRecibe,
  // reloadPersonalEntrega,
  onAdd,
  onCancel,
  onSave,
  onPreCompraSelected
}: PreCompraFormProps) => {
  const esEditar = !esAgregar;
  const activo = esAgregar || precompra?.activa === true;
  const inactivo = !activo || precompra?.esCompra === true;
  const tipo = precompra?.esCompra === true ? 'COMPRA' : 'PRE-COMPRA';
  const [rows, setRows] = useState<GridRowsProp<AnimalPreCompraRow>>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [showPersonalEntregaSearchDialog, setShowPersonalEntregaSearchDialog] = useState(false);
  const [showPersonalRecibeSearchDialog, setShowPersonalRecibeSearchDialog] = useState(false);
  const [showPersonalVendeSearchDialog, setShowPersonalVendeSearchDialog] = useState(false);
  const [showPersonalEntregaAddDialog, setShowPersonalEntregaAddDialog] = useState(false);
  const [showPersonalRecibeAddDialog, setShowPersonalRecibeAddDialog] = useState(false);
  const [showPersonalVendeAddDialog, setShowPersonalVendeAddDialog] = useState(false);
  const [showPreComprasDialog, setShowPreComprasDialog] = useState(false);
  const [showConfirmSave, setShowConfirmSave] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const [showHistorialSiiniga, setShowHistorialSiiniga] = useState(false);
  const [showHistorialTemporal, setShowHistorialTemporal] = useState(false);
  const { loading, showWarningToast } = useUIStore();

  const initialValues: PreCompraFormValues = {
    folioInterno: precompra?.folioInterno ?? '',
    fecha: precompra.fecha ? dayjs(precompra.fecha) : dayjs(new Date()),
    referencia: precompra?.referencia ?? '',
    idPersonaVende: precompra?.idPersonaVende ?? 0,
    vendidoPor: precompra?.vendidoPor ?? '',
    idPersonaRecibe: precompra?.idPersonaRecibe ?? 0,
    recibidoPor: precompra?.recibidoPor ?? '',
    idPersonaEntrega: precompra?.idPersonaEntrega ?? 0,
    entregadoPor: precompra?.entregadoPor ?? '',
    idCorral: precompra?.idCorral ?? 1,
    idZona: 1,
    totalAnimales: precompra?.totalAnimales ?? 0,
    animales: convertToAnimalesPreCompraRow(precompra)
  };

  const validationSchemaEx = esAgregar ? validationSchema : validationSchema.omit(['idZona']);

  const form = useFormik({
    initialValues,
    validationSchema: validationSchemaEx,
    onSubmit: async (values) => {
      const result = await onSave(values);
      if (result) {
        reset();
      }
    }
  });

  const corralesOptions = transformToOptions(corrales);
  const zonasOptions = transformToOptions(zonas);

  const addRow = () => {
    if (form.values.totalAnimales === 0) {
      showWarningToast('Debe capturar el número de animales antes de agregar partidas');
      return;
    }
    if (rows.length >= form.values.totalAnimales) {
      showWarningToast('El número de partidas no puede ser mayor al número de animales');
      return;
    }

    const id = generateRandonId();
    const newAnimal: AnimalPreCompra = {
      id,
      partida: rows.length + 1,
      areteSiiniga: '',
      areteTemporal: '',
      tienePapeles: false,
      reemo: '',
      idTipoGanado: 0,
      kgCompra: 0,
      kgLlegada: 0
    };
    const newRow: AnimalPreCompraRow = {
      ...newAnimal,
      isNew: true,
      folio: precompra?.folioInterno ?? ''
    };

    form.setFieldValue('animales', [...form.values.animales, newAnimal]);
    setRows((prev) => [...prev, newRow]);
    setRowModesModel((prevRowModesModel) => ({
      ...prevRowModesModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'areteSiiniga' }
    }));

    form.setFieldTouched('animales', true);
  };
  const handleEditRow = useCallback((id: GridRowId) => {
    setRowModesModel((prevRowModesModel) => ({
      ...prevRowModesModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'areteSiiniga' }
    }));
  }, []);
  const handleSaveRow = useCallback((id: GridRowId) => {
    setRowModesModel((prevRowModesModel) => ({
      ...prevRowModesModel,
      [id]: { mode: GridRowModes.View }
    }));
  }, []);
  const handleDeleteRow = useCallback(
    async (id: GridRowId) => {
      setRows((prev) => prev.filter((row) => row.id !== id));

      await form.setFieldValue(
        'animales',
        form.values.animales.filter((row) => row.id !== id)
      );
      await form.setFieldTouched('animales', true);
    },
    [form]
  );
  const handleCancel = useCallback(
    (id: GridRowId) => {
      setRowModesModel((prevRowModesModel) => ({
        ...prevRowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true }
      }));

      const row = rows.find((r) => r.id === id);
      if (row?.isNew) {
        setRows((prev) => prev.filter((r) => r.id !== id));
        form.setFieldValue(
          'animales',
          form.values.animales.filter((r) => r.id !== id)
        );
      }
    },
    [form]
  );
  const handleRowUpdate = useCallback(
    async (newRow: AnimalPreCompraRow) => {
      const updatedRow = { ...newRow, isNew: false };
      setRows((prev) => prev.map((row) => (row.id === newRow.id ? updatedRow : row)));
      await form.setFieldValue(
        'animales',
        form.values.animales.map((row) => (row.id === newRow.id ? updatedRow : row))
      );
      return updatedRow;
    },
    [form.values]
  );

  const handlePersonaEntregaSelect = async (registro: PersonaEntregaResumen) => {
    await form.setFieldValue('idPersonaEntrega', registro.id, true);
    await form.setFieldValue('entregadoPor', registro.nombre, true);
    setShowPersonalEntregaSearchDialog(false);
  };
  const handlePersonaRecibeSelect = async (registro: PersonaRecibeResumen) => {
    await form.setFieldValue('idPersonaRecibe', registro.id, true);
    await form.setFieldValue('recibidoPor', registro.nombre);
    setShowPersonalRecibeSearchDialog(false);
  };
  const handlePersonaVendeSelect = async (registro: PersonaVendeResumen) => {
    await form.setFieldValue('idPersonaVende', registro.id, true);
    await form.setFieldValue('vendidoPor', registro.nombre);
    setShowPersonalVendeSearchDialog(false);
  };
  const handlePreComprasSelect = async (precompra: PreCompraBusqueda) => {
    setShowPreComprasDialog(false);
    onPreCompraSelected(precompra);
  };

  const handleConfirmSave = () => {
    setShowConfirmSave(false);

    form.handleSubmit();
    if (!form.isValid) {
      if (typeof form.errors.animales === 'string') {
        showWarningToast(form.errors.animales.toString());
      } else {
        showWarningToast(
          'Aún faltan datos obligatorios en su captura, de favor verifíquelo para poder continuar'
        );
      }
    }
  };
  const handleConfirmCancel = async () => {
    setShowConfirmCancel(false);
    onCancel && (await onCancel());
  };

  const handleClickHistorialSiiniga = (folio: string, partida: number) => {
    cargarHistorialSiiniga(folio, partida).then(() => setShowHistorialSiiniga(true));
  };
  const handleClickHistorialTemporal = (folio: string, partida: number) => {
    cargarHistorialTemporal(folio, partida).then(() => setShowHistorialTemporal(true));
  };

  const handleAddPersonalEntrega = async (newID: number) => {
    setShowPersonalEntregaAddDialog(false);
    // await reloadPersonalEntrega();
    await form.setFieldValue('idPersonaEntrega', newID);
  };
  const handleAddPersonalRecibe = async (newID: number) => {
    setShowPersonalRecibeAddDialog(false);
    // await reloadPersonalRecibe();
    await form.setFieldValue('idPersonaRecibe', newID);
  };
  const handleAddPersonalVende = async (newID: number) => {
    setShowPersonalVendeAddDialog(false);
    // await reloadPersonalVende();
    await form.setFieldValue('idPersonaVende', newID);
  };

  useEffect(() => {
    const animales = convertToAnimalesPreCompraRow(precompra);
    setRows(animales);
    setRowModesModel(convertToRowsModel(animales));
    form.setValues({
      ...initialValues,
      animales
    });
  }, [precompra.folioInterno, precompra.partidas]);

  const reset = async () => {
    if (esEditar) {
      form.setValues(initialValues);
      const rows = initialValues.animales.map((a, i) => ({
        ...a,
        isNew: false,
        folio: initialValues.folioInterno,
        partida: i + 1
      }));
      setRows(rows);
      setRowModesModel(convertToRowsModel(rows));
    } else {
      form.resetForm();
      setRows([]);
      setRowModesModel({});
      await form.setFieldValue('folioInterno', precompra.folioInterno);
    }
  };

  return (
    <>
      <AppForm title="Pre-compra de Ganado" lineSpacing={1}>
        <Grid>
          <Grid container spacing={2}>
            <Grid size={3}>
              <AppTextField
                name="folioInterno"
                label="Folio Interno"
                value={form.values.folioInterno}
                disabled={true}
                endAdornment={
                  <IconButton onClick={() => setShowPreComprasDialog(true)}>
                    <SearchIcon />
                  </IconButton>
                }
              />
            </Grid>
            <Grid size={2}>
              <DatePicker
                label="Fecha"
                value={form.values.fecha}
                disabled={loading || inactivo}
                onChange={(date) => form.setFieldValue('fecha', date)}
              />
            </Grid>
            <Grid size={3}>
              <AppTextField
                label="Referencia"
                name="referencia"
                disabled={loading || inactivo}
                value={form.values.referencia}
                maxLength={25}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Grid container spacing={2}>
            <Grid size={5}>
              <AppTextField
                label="Vendido Por"
                value={form.values.vendidoPor}
                readOnly={true}
                endAdornment={
                  inactivo || loading ? (
                    <></>
                  ) : (
                    <>
                      <IconButton onClick={() => setShowPersonalVendeSearchDialog(true)}>
                        <SearchIcon />
                      </IconButton>
                      <IconButton onClick={() => setShowPersonalVendeAddDialog(true)}>
                        <AddIcon />
                      </IconButton>
                    </>
                  )
                }
                error={form.touched.idPersonaVende && Boolean(form.errors.idPersonaVende)}
                helperText={form.touched.idPersonaVende ? form.errors.idPersonaVende : ''}
              />
            </Grid>
            <Grid size={3}>
              <ComboBox
                label="Corral de reciba"
                name="idCorral"
                disabled={loading || inactivo}
                value={form.values.idCorral}
                options={corralesOptions}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                error={form.touched.idCorral && Boolean(form.errors.idCorral)}
                helperText={form.touched.idCorral ? form.errors.idCorral : ''}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Grid container spacing={2}>
            <Grid size={5}>
              <AppTextField
                label="Recibido Por"
                value={form.values.recibidoPor}
                readOnly={true}
                endAdornment={
                  inactivo || loading ? (
                    <></>
                  ) : (
                    <>
                      <IconButton onClick={() => setShowPersonalRecibeSearchDialog(true)}>
                        <SearchIcon />
                      </IconButton>
                      <IconButton onClick={() => setShowPersonalRecibeAddDialog(true)}>
                        <AddIcon />
                      </IconButton>
                    </>
                  )
                }
                error={form.touched.idPersonaRecibe && Boolean(form.errors.idPersonaRecibe)}
                helperText={form.touched.idPersonaRecibe ? form.errors.idPersonaRecibe : ''}
              />
            </Grid>
            <Grid size={3}>
              <AppTextField
                name="totalAnimales"
                label="Número de animales"
                value={form.values.totalAnimales}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                disabled={loading || inactivo}
                error={form.touched.totalAnimales && Boolean(form.errors.totalAnimales)}
                helperText={form.touched.totalAnimales ? form.errors.totalAnimales : ''}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Grid container spacing={2}>
            <Grid size={5}>
              <AppTextField
                label="Entregado Por"
                value={form.values.entregadoPor}
                readOnly={true}
                endAdornment={
                  inactivo || loading ? (
                    <></>
                  ) : (
                    <>
                      <IconButton onClick={() => setShowPersonalEntregaSearchDialog(true)}>
                        <SearchIcon />
                      </IconButton>
                      <IconButton onClick={() => setShowPersonalEntregaAddDialog(true)}>
                        <AddIcon />
                      </IconButton>
                    </>
                  )
                }
                error={form.touched.idPersonaEntrega && Boolean(form.errors.idPersonaEntrega)}
                helperText={form.touched.idPersonaEntrega ? form.errors.idPersonaEntrega : ''}
              />
            </Grid>
            {esAgregar && (
              <Grid size={3}>
                <ComboBox
                  label="Zona"
                  name="idZona"
                  disabled={loading || inactivo}
                  value={form.values.idZona!}
                  options={zonasOptions}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  error={form.touched.idZona && Boolean(form.errors.idZona)}
                  helperText={form.touched.idZona ? form.errors.idZona : ''}
                />
              </Grid>
            )}
            <Grid size={3}>
              <AppTextField label="Tipo de documento" value={tipo} disabled={true} />
            </Grid>
          </Grid>
        </Grid>
        <Grid>{loading ? <LinearProgress color="secondary" /> : <></>}</Grid>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            color="secondary"
            disabled={loading || inactivo}
            onClick={addRow}
            disableElevation
          >
            Agregar
          </Button>
        </Stack>
        <Grid>
          <AnimalesPreCompra
            tiposGanado={tiposGanado}
            readonly={inactivo}
            esAgregar={esAgregar}
            rows={rows}
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            onClickHistorialSiiniga={handleClickHistorialSiiniga}
            onClickHistorialTemporal={handleClickHistorialTemporal}
            onCancel={handleCancel}
            onDelete={handleDeleteRow}
            onEdit={handleEditRow}
            onSave={handleSaveRow}
            onRowUpdate={handleRowUpdate}
          />
        </Grid>
        <Divider />
        <Grid>
          <Grid container spacing={2} justifyContent="flex-end">
            {esEditar ? (
              <Grid>
                <Button variant="contained" onClick={onAdd} disabled={loading} disableElevation>
                  Nueva
                </Button>
              </Grid>
            ) : (
              <></>
            )}
            <Grid>
              <Button
                type="button"
                variant="contained"
                color="secondary"
                startIcon={<CheckCircleIcon />}
                onClick={() => setShowConfirmSave(true)}
                disabled={loading || inactivo}
                disableElevation
              >
                Guardar
              </Button>
            </Grid>
            <Grid>
              <Button
                variant="contained"
                startIcon={esEditar ? <CancelIcon /> : <CleaningServicesIcon />}
                disabled={loading || inactivo}
                disableElevation
                onClick={reset}
              >
                {esEditar ? 'Deshacer cambios' : 'Limpiar'}
              </Button>
            </Grid>
            {esEditar ? (
              <Grid>
                <Button
                  variant="contained"
                  onClick={() => setShowConfirmCancel(true)}
                  startIcon={<DeleteIcon />}
                  disabled={loading || inactivo}
                  disableElevation
                >
                  Cancelar
                </Button>
              </Grid>
            ) : (
              <></>
            )}
            <Grid>
              <Button
                variant="contained"
                startIcon={<ExitToAppIcon />}
                component={RouterLink}
                to="/"
                disableElevation
              >
                Salir
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </AppForm>
      <PersonalEntregaSearchDialog
        open={showPersonalEntregaSearchDialog}
        onClose={() => setShowPersonalEntregaSearchDialog(false)}
        onSelect={handlePersonaEntregaSelect}
      />
      <PersonalEntregaAddDialog
        open={showPersonalEntregaAddDialog}
        onAddSuccess={handleAddPersonalEntrega}
        onClose={() => setShowPersonalEntregaAddDialog(false)}
      />
      <PersonalRecibeSearchDialog
        open={showPersonalRecibeSearchDialog}
        onClose={() => setShowPersonalRecibeSearchDialog(false)}
        onSelect={handlePersonaRecibeSelect}
      />
      <PersonalRecibeAddDialog
        open={showPersonalRecibeAddDialog}
        onAddSuccess={handleAddPersonalRecibe}
        onClose={() => setShowPersonalRecibeAddDialog(false)}
      />
      <PersonalVendeSearchDialog
        open={showPersonalVendeSearchDialog}
        onClose={() => setShowPersonalVendeSearchDialog(false)}
        onSelect={handlePersonaVendeSelect}
      />
      <PersonalVendeAddDialog
        open={showPersonalVendeAddDialog}
        onAddSuccess={handleAddPersonalVende}
        onClose={() => setShowPersonalVendeAddDialog(false)}
      />
      <PreCompraSearchDialog
        open={showPreComprasDialog}
        onClose={() => setShowPreComprasDialog(false)}
        onSelect={handlePreComprasSelect}
      />
      <AppDialog
        open={showConfirmSave}
        onClose={() => setShowConfirmSave(false)}
        title="Confirme"
        message={
          esAgregar
            ? '¿Está seguro de guardar la Pre-Compra?'
            : '¿Está seguro de actualizar la Pre-Compra?'
        }
        buttons={
          <>
            <Button onClick={handleConfirmSave}>Sí, guardar</Button>
            <Button onClick={() => setShowConfirmSave(false)}>No</Button>
          </>
        }
      />
      <AppDialog
        open={showConfirmCancel}
        onClose={() => setShowConfirmCancel(false)}
        title="Confirme"
        message="¿Está seguro de cancelar la Pre-Compra?"
        buttons={
          <>
            <Button onClick={handleConfirmCancel}>Sí, cancelar</Button>
            <Button onClick={() => setShowConfirmCancel(false)}>No</Button>
          </>
        }
      />
      <HistorialAreteDialog
        titulo="SIINIGA"
        historial={historialSiiniga}
        open={showHistorialSiiniga}
        onClose={() => setShowHistorialSiiniga(false)}
        formatter={(arete) => arete.replace(/(\d{2})(\d{4})(\d{4})/, '$1-$2-$3')}
      />
      <HistorialAreteDialog
        titulo="TEMPORAL"
        historial={historialTemporal}
        open={showHistorialTemporal}
        onClose={() => setShowHistorialTemporal(false)}
        formatter={(arete) => arete.replace(/([a-zA-Z]{1})([0-9]{1,3})/, '$1-$2')}
      />
    </>
  );
};

export default PreCompraForm;
